import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle, Error, ExpandLess, ExpandMore, Info, Translate } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { BulkTranslateResult } from './types';
import { getLanguageDisplayName } from '../product-details/utils';

interface BulkResultsDialogProps {
  open: boolean;
  result: BulkTranslateResult | null;
  targetLanguage: string;
  onClose: () => void;
}

export const BulkResultsDialog: React.FC<BulkResultsDialogProps> = ({
  open,
  result,
  targetLanguage,
  onClose,
}) => {
  const { t } = useTranslation();
  const [showFailures, setShowFailures] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);

  if (!result) return null;

  const { translated_count, skipped_count, failed_count, results } = result;
  const failedItems = results.filter((r) => r.status === 'failed');
  const skippedItems = results.filter((r) => r.status === 'skipped');
  const hasFailures = failed_count > 0;
  const allFailed = translated_count === 0 && failed_count > 0;

  const targetName = getLanguageDisplayName(targetLanguage);

  const statusConfig = allFailed
    ? { severity: 'error' as const, title: t('products.bulk.results.titleFailed', 'Translation failed') }
    : hasFailures
    ? { severity: 'warning' as const, title: t('products.bulk.results.titlePartial', 'Partially complete') }
    : { severity: 'success' as const, title: t('products.bulk.results.titleSuccess', 'Translation complete') };

  const skipReasonLabel = (reason?: string) => {
    if (reason === 'already_exists') return t('products.bulk.results.skipReasonExists', 'Already has {{lang}} translation', { lang: targetName });
    if (reason === 'no_source') return t('products.bulk.results.skipReasonNoSource', 'No source translation found');
    return reason ?? t('products.bulk.results.skipReasonUnknown', 'Skipped');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Translate color="primary" />
        {statusConfig.title}
      </DialogTitle>
      <DialogContent>
        <Alert severity={statusConfig.severity} sx={{ mb: 2.5 }} icon={false}>
          <Typography variant="body2">
            {t(
              'products.bulk.results.summary',
              'Results for translation to {{language}}',
              { language: targetName }
            )}
          </Typography>
        </Alert>

        <Stack direction="row" spacing={1.5} mb={2.5} flexWrap="wrap">
          {translated_count > 0 && (
            <Chip
              icon={<CheckCircle fontSize="small" />}
              label={t('products.bulk.results.translated', '{{count}} translated', { count: translated_count })}
              color="success"
              size="small"
              variant="outlined"
            />
          )}
          {skipped_count > 0 && (
            <Chip
              icon={<Info fontSize="small" />}
              label={t('products.bulk.results.skipped', '{{count}} skipped', { count: skipped_count })}
              color="default"
              size="small"
              variant="outlined"
            />
          )}
          {failed_count > 0 && (
            <Chip
              icon={<Error fontSize="small" />}
              label={t('products.bulk.results.failed', '{{count}} failed', { count: failed_count })}
              color="error"
              size="small"
              variant="outlined"
            />
          )}
        </Stack>

        {failedItems.length > 0 && (
          <Box mb={1.5}>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ cursor: 'pointer', mb: 0.5 }}
              onClick={() => setShowFailures((v) => !v)}
            >
              <Typography variant="body2" fontWeight={600} color="error.dark">
                {t('products.bulk.results.failedDetails', 'Failed products')}
              </Typography>
              {showFailures ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
            <Collapse in={showFailures}>
              <List dense disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {failedItems.map((item, idx) => (
                  <React.Fragment key={item.product_id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem>
                      <ListItemText
                        primary={item.product_name || item.product_id}
                        secondary={item.error_detail || item.error_code}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: 'caption', color: 'error.main' }}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </Box>
        )}

        {skippedItems.length > 0 && (
          <Box>
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ cursor: 'pointer', mb: 0.5 }}
              onClick={() => setShowSkipped((v) => !v)}
            >
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {t('products.bulk.results.skippedDetails', 'Skipped products')}
              </Typography>
              {showSkipped ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>
            <Collapse in={showSkipped}>
              <List dense disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {skippedItems.map((item, idx) => (
                  <React.Fragment key={item.product_id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem>
                      <ListItemText
                        primary={item.product_name || item.product_id}
                        secondary={skipReasonLabel(item.skip_reason)}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          {t('common.done', 'Done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
